import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { isObservable, Observable, of } from 'rxjs';
import { NgSelectComponent, NgSelectModule } from '@ng-select/ng-select';
import { NgIf, NgTemplateOutlet, AsyncPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { TooltipDirective } from 'src/app/standalones/directives/pncl-tooltip.directive';

/**
 * PencilSearchableDropdownComponent
 *
 * A flexible, searchable dropdown component that can be used with any data type.
 * The component supports custom item templates and filtering functionality.
 * Tooltip is displayed only when bindLabel is provided.
 *
 * Important Note:
 * bindValue is required to make ng-select work with the selected item.
 *
 * @example Basic usage:
 * ```html
 * <pencil-searchable-dropdown
 *   [dataSource]="items$"
 *   [search]="true"
 *   placeholder="Select an item"
 *   (valueChange)="onItemSelected($event)">
 * </pencil-searchable-dropdown>
 * ```
 *
 * @example With custom template:
 * ```html
 * <pencil-searchable-dropdown
 *   [dataSource]="users$"
 *   [search]="true"
 *   [customFilterFn]="filterUsers"
 *   [itemTpl]="userTemplate"
 *   [bindValue]="'id'"
 *   placeholder="Select a user"
 *   (valueChange)="onUserSelected($event)">
 * </pencil-searchable-dropdown>
 *
 * <ng-template #userTemplate let-user>
 *   <div class="user-item">
 *     <img [src]="user.avatar" />
 *     <span>{{ user.name }}</span>
 *   </div>
 * </ng-template>
 * ```
 *
 * ```typescript
 * filterUsers(term: string, user: User): boolean {
 *   return user.name.toLowerCase().includes(term.toLowerCase()) ||
 *          user.email.toLowerCase().includes(term.toLowerCase());
 * }
 * ```
 */
@Component({
  selector: 'app-pencil-searchable-dropdown[bindValue]',
  templateUrl: './pencil-searchable-dropdown.component.html',
  styleUrls: ['./pencil-searchable-dropdown.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    NgSelectModule,
    NgIf,
    NgTemplateOutlet,
    AsyncPipe,
    FormsModule,
    TranslateModule,
    TooltipDirective,
  ],
})
export class PencilSearchableDropdownComponent<T> {
  @ViewChild('dropdown') private dropdown?: NgSelectComponent;

  /** Internal stream always used by the template */
  protected _items$!: Observable<T[]>;

  @Input() selectedItem: T | null = null;

  /** Accepts either `T[]` _or_ `Observable<T[]>` */
  @Input() set dataSource(src: T[] | Observable<T[]>) {
    this._items$ = isObservable(src) ? src : of(src);
  }

  /**
   * Enables/disables the search functionality.
   */
  @Input() searchable = true;

  /**
   * Custom search function for filtering dropdown dropdownItems.
   * Return true to include the dropdownItem, false to exclude.
   */
  @Input() customSearchFn?: (searchText: string, dropdownItem: T) => boolean;

  /**
   * Template for rendering dropdown option items.
   */
  @Input() itemTpl?: TemplateRef<{ $implicit: T }>;

  /**
   * Property name to extract as the value from objects.
   * This value is required to make ng-select work with the selected item.
   */
  @Input() bindValue!: string;

  /**
   * Property name to display as text for each item.
   */
  @Input() bindLabel = '';

  /**
   * Text displayed when no item is selected.
   */
  @Input() placeholder = '';

  /**
   * Whether the dropdown is disabled.
   */
  @Input() disabled = false;

  /**
   * Whether tooltips should be disabled.
   */
  @Input() disableTooltip = false;

  /**
   * Whether the dropdown has an error state.
   */
  @Input() hasError = false;

  /**
   * Position of the dropdown. Can be 'auto', 'top', or 'bottom'.
   * Default is 'auto' which automatically positions based on available space.
   */
  @Input() dropdownPosition: 'auto' | 'top' | 'bottom' = 'auto';

  /**
   * Event emitted when the selected value changes. Emits the selected item.
   */
  @Output() readonly selectedItemChanged = new EventEmitter<T>();

  /**
   * Handles selection changes from the dropdown.
   * Updates internal value, calls form callbacks, and emits valueChange event.
   */
  onChange(selectedItem: T): void {
    this.selectedItemChanged.emit(selectedItem);
    this.selectedItem = selectedItem;
    this.dropdown?.blur();
  }

  /**
   * Gets the label text for the selected item
   */
  getLabel(item: T | null): string | null {
    if (!item || !this.bindLabel) {
      return null;
    }

    // Return the value from bindLabel property
    return String((item as any)[this.bindLabel]);
  }
}
